import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import Text from "@components/Text";
import Button from "@components/Button";
import SizedBox from "@components/SizedBox";
import { useLang } from "@src/hooks/useLang";
import IntegrationCard from "@screens/ProfileScreen/IntegrationCard";
import { ConnectedApp, disconnectApp, getConnectedApps } from "@services/connectedAppsService";

const AppRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-top: 1px solid #ebeeef;
`;

const AppText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "");

/**
 * Settings: AI assistants (ChatGPT/Claude) connected through the MCP connector,
 * each with Disconnect. They belong to the logged-in person, not the account on
 * screen, so it shows to a team member viewing an owner's account too (the
 * /me/connected-apps routes key on the actor). The consent screen promises this, and
 * Disconnect is the only full revoke: refresh tokens renew on use and outlive
 * a password change. Only the list — the "add GreenGrowth to your assistant"
 * card was taken out on 2026-10-18 — so it shows only while there is a
 * connection to remove.
 */
const ConnectedAppsSection: React.FC = () => {
  const lang = useLang();
  const t = lang.aiConnector;
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getConnectedApps()
      .then((res) => !cancelled && setApps(res.apps ?? []))
      .catch(() => {
        /* the card simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (apps.length === 0) return null;

  const disconnect = async (app: ConnectedApp) => {
    setRemoving(app.clientId);
    try {
      await disconnectApp(app.clientId);
      setApps((prev) => prev.filter((a) => a.clientId !== app.clientId));
      toast.success(t.disconnected);
    } catch {
      toast.error(t.disconnectFailed);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <IntegrationCard title={t.cardTitle} connected connectedLabel={t.cardConnected}>
      <SizedBox height={4} />
      {apps.map((app) => (
        <AppRow key={app.clientId}>
          <AppText>
            <Text size={14} weight={500}>
              {app.name ?? t.unknownApp}
              {app.host ? ` (${app.host})` : ""}
            </Text>
            <Text size={12} color="#899093">
              {[
                app.connectedAt ? t.connectedOn.replace("{date}", formatDate(app.connectedAt)) : null,
                app.lastUsedAt ? t.lastUsed.replace("{date}", formatDate(app.lastUsedAt)) : t.neverUsed,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </AppText>
          <Button
            kind="outline"
            onClick={() => disconnect(app)}
            disabled={removing === app.clientId}
            style={{ width: "auto", flexShrink: 0 }}
          >
            {t.disconnect}
          </Button>
        </AppRow>
      ))}
    </IntegrationCard>
  );
};

export default ConnectedAppsSection;
